import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for scroll-triggered animations using Intersection Observer.
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1) to trigger animation
 * @param {string} options.rootMargin - Margin around the root element
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @returns {[React.RefObject, boolean]} - Ref to attach and visibility state
 */
export function useScrollAnimation({
  threshold = 0.1,
  rootMargin = "0px 0px -50px 0px",
  triggerOnce = true,
} = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible];
}

/**
 * Returns animation classes based on visibility state.
 * @param {boolean} isVisible - Whether the element is visible
 * @param {string} animation - Animation type: 'fadeUp', 'fadeIn', 'fadeLeft', 'fadeRight', 'scale'
 * @param {number} delay - Animation delay in milliseconds
 * @returns {string} - CSS classes for animation
 */
export function getAnimationClasses(isVisible, animation = "fadeUp", delay = 0) {
  const baseClasses = "transition-all duration-700 ease-out";
  const delayClass = delay > 0 ? `delay-[${delay}ms]` : "";
  
  const animations = {
    fadeUp: isVisible 
      ? "opacity-100 translate-y-0" 
      : "opacity-0 translate-y-8",
    fadeIn: isVisible 
      ? "opacity-100" 
      : "opacity-0",
    fadeLeft: isVisible 
      ? "opacity-100 translate-x-0" 
      : "opacity-0 -translate-x-8",
    fadeRight: isVisible 
      ? "opacity-100 translate-x-0" 
      : "opacity-0 translate-x-8",
    scale: isVisible 
      ? "opacity-100 scale-100" 
      : "opacity-0 scale-95",
  };

  return `${baseClasses} ${delayClass} ${animations[animation] || animations.fadeUp}`;
}
